class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.mobileOffsetHandler = this.syncMobileDrawerOffset.bind(this);
    window.addEventListener('resize', this.mobileOffsetHandler, { passive: true });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', this.mobileOffsetHandler, { passive: true });
    if (window.BrilintonSurfaceManager) {
      window.BrilintonSurfaceManager.register('cart', {
        root: () => this,
        close: () => this.close(),
      });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    this.setHeaderCartIconState(this.classList.contains('active'));

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.classList.contains('active') ? this.close() : this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.classList.contains('active') ? this.close() : this.open(cartLink);
      }
    });
  }

  setHeaderCartIconState(isOpen) {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.classList.toggle('is-active', isOpen);
    cartLink.setAttribute('aria-expanded', String(isOpen));
    cartLink.setAttribute('aria-label', isOpen ? 'Close cart' : 'Open cart');

    const label = cartLink.querySelector('[data-cart-icon-label]');
    if (label) label.textContent = isOpen ? 'Close cart' : 'Open cart';
  }

  resetMerchandiseScroll() {
    const scroller = this.querySelector('cart-drawer-items');
    if (!scroller) return;
    scroller.scrollTop = 0;
  }

  getVisibleMobileHeaderBottom() {
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return 0;

    const selectors = [
      '.section-header.shopify-section-group-header-group',
      '.shopify-section-group-header-group.section-header',
      '#shopify-section-header',
      'sticky-header',
      'header.header',
      '.announcement-bar-section',
      '#announcement-bar',
      '.announcement-bar'
    ];

    let bottom = 0;
    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (!element || !element.getBoundingClientRect) return;
        const style = window.getComputedStyle ? window.getComputedStyle(element) : null;
        if (style && (style.display === 'none' || style.visibility === 'hidden')) return;
        const rect = element.getBoundingClientRect();
        if (rect.width < 1 || rect.height < 1) return;
        if (rect.bottom <= 0 || rect.top > 160) return;
        bottom = Math.max(bottom, rect.bottom);
      });
    });

    return Math.max(0, Math.min(Math.round(bottom), 180));
  }

  syncMobileDrawerOffset(force) {
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) {
      this.style.removeProperty('--cart-mobile-top-offset');
      return;
    }
    if (!force && !this.classList.contains('active')) return;
    const measured = this.getVisibleMobileHeaderBottom();
    const fallback = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 58;
    this.style.setProperty('--cart-mobile-top-offset', `${Math.max(0, measured || fallback)}px`);
  }

  open(triggeredBy) {
    const surfaceManager = window.BrilintonSurfaceManager;
    const surfaceTrigger = triggeredBy || (!surfaceManager?.isOpen('cart') ? document.activeElement : null);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (surfaceManager) {
      const surfaceOptions = { lockMode: 'overflow', preservePeers: ['pdp-studio'] };
      if (surfaceTrigger) surfaceOptions.trigger = surfaceTrigger;
      surfaceManager.open('cart', surfaceOptions);
    }
    this.setHeaderCartIconState(true);
    this.syncMobileDrawerOffset(true);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // R44-178: start the panel on the next paint; no timing guess is needed.
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
      this.resetMerchandiseScroll();
    });

    this.addEventListener(
      'transitionend',
      () => {
        // A manual credit claim can hand Cart to My Brilinton mid-transition.
        if (!this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    if (!window.BrilintonSurfaceManager) document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.setHeaderCartIconState(false);
    if (window.BrilintonSurfaceManager) {
      removeTrapFocus();
      window.BrilintonSurfaceManager.close('cart', { restoreFocus: true });
    } else {
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }
  }

  disconnectedCallback() {
    if (this.mobileOffsetHandler) {
      window.removeEventListener('resize', this.mobileOffsetHandler);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.mobileOffsetHandler);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    window.BrilintonCartSummary?.syncAll();

    requestAnimationFrame(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
